import { Label } from '@/components/ui/label';
import { useT } from '@/i18n/LanguageContext';
import { CommonRegistrationBlock, missingFields } from './CommonRegistrationBlock';
import { InspectionFields } from './InspectionFields';
import { FormSection } from './TechsFields';

// 種別のラベルは製造の辞書（`mfg.eq.kind.*`）が正本。定義をここに複製しない。
export const EQUIPMENT_KIND_OPTIONS = ['machine', 'tool', 'jig', 'gauge']
  .map((value) => ({ value, key: `mfg.eq.kind.${value}` }));

// 設備の共通ブロックで使う項目（仕様 2.6 ＋ TECHS 整合）。
export const EQUIPMENT_FIELDS = [
  'master_search', 'name', 'category', 'kind', 'maker', 'serial_no', 'notes',
  'number', 'sku_group', 'order_category', 'model_no', 'maker_pn', 'uom', 'supplier',
  'reference_url', 'location', 'department', 'status', 'owner', 'acquired_on', 'price',
  'attachments',
];

// ＊ と 要入力 の対象。**保存は止めない**（仕様 4）。
// R1: 部署は番号の記号（SNS/EQP…）を決めるので必須。
export const EQUIPMENT_REQUIRED = [
  'name', 'category', 'model_no', 'location', 'number', 'order_category', 'uom', 'department',
];

export const missingRequired = (v) => missingFields(v, EQUIPMENT_REQUIRED);

/**
 * <EquipmentRegistrationFields> — F2（技術 設備台帳）と F3（製造 設備・工具）が
 * **同じ部品・同じ並び・同じ名前**で使う登録ブロック。
 *
 * 共通ブロック（`CommonRegistrationBlock`）＋ 設備だけの追加ブロック
 * （点検・校正 / 由来プロジェクト）。
 */
export const EquipmentRegistrationFields = ({
  value, onChange, editing = false, currentNo = '',
  categories = [], departments = [], employees = [], projects = [],
  statusOptions = [], locationDepartmentId = null, register = 'equip', appCode = '',
  deptCode = '',
  askAcquisitionType = false, departmentName = '',
  files = [], onFilesChange, onUpload, testPrefix = 'equip-form',
}) => {
  const { t } = useT();
  const patch = (p) => onChange({ ...value, ...p });
  const orphanProject = (value?.source_project_id
    && !projects.some((p) => p.id === value.source_project_id))
    ? { id: value.source_project_id, name: value.source_project_name || value.source_project_id }
    : null;

  return (
    <CommonRegistrationBlock
      value={value} onChange={onChange} editing={editing}
      fields={EQUIPMENT_FIELDS} requiredKeys={EQUIPMENT_REQUIRED}
      askAcquisitionType={askAcquisitionType} departmentName={departmentName}
      register={register} appCode={appCode} currentNo={currentNo}
      categories={categories} departments={departments} employees={employees}
      statusOptions={statusOptions} locationDepartmentId={locationDepartmentId}
      kindOptions={EQUIPMENT_KIND_OPTIONS} deptCode={deptCode}
      files={files} onFilesChange={onFilesChange} onUpload={onUpload}
      testPrefix={testPrefix}
      extra={(
        <>
          <InspectionFields value={value} onChange={(next) => patch(next)} employees={employees}
            testPrefix={`${testPrefix}-inspection`} />

          <FormSection title={t('reg.project')} testId={`${testPrefix}-section-project`}>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('reg.project')}</Label>
              <select value={value?.source_project_id || ''}
                onChange={(e) => patch({ source_project_id: e.target.value })}
                className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
                data-testid={`${testPrefix}-project`}>
                <option value="">{t('reg.none')}</option>
                {orphanProject && (
                  <option value={orphanProject.id} data-testid={`${testPrefix}-project-archived`}>
                    {t('reg.project.archived', { name: orphanProject.name })}
                  </option>
                )}
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-zinc-400">{t('reg.project.hint')}</p>
            </div>
          </FormSection>
        </>
      )}
    />
  );
};

export default EquipmentRegistrationFields;
